import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  Cloud, 
  Sparkles, 
  Loader2, 
  Lock,
  RefreshCw,
  TrendingUp,
  Eye,
  GitCompare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { TopNav } from '../components/BottomNav';
import {
  getIntelligenceOverview,
  getSymbolDictionary,
  generateSymbolInsight,
  getDreamClimate,
  generateClimateReflection,
  getGrowthThreads,
  discoverGrowthThreads,
  getDreamEvolution,
  analyzeDreamEvolution
} from '../lib/api';
import { toast } from 'sonner';

const ONIRVA_ICON = "https://customer-assets.emergentagent.com/job_200ec0e0-0820-4417-b1b5-b6286eb18bc4/artifacts/c3nyk9fu_80AC8250-CA42-4BF9-BA0D-F1211AA6A764.png";

export default function Intelligence() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [activeTab, setActiveTab] = useState('symbols');
  const [symbols, setSymbols] = useState([]);
  const [climate, setClimate] = useState(null);
  const [threads, setThreads] = useState([]);
  const [evolution, setEvolution] = useState(null);
  const [generatingInsight, setGeneratingInsight] = useState(null);
  const [generatingReflection, setGeneratingReflection] = useState(false);
  const [discoveringThreads, setDiscoveringThreads] = useState(false);
  const [analyzingEvolution, setAnalyzingEvolution] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const results = await Promise.allSettled([
        getIntelligenceOverview(),
        getSymbolDictionary(),
        getDreamClimate('month'),
        getGrowthThreads(),
        getDreamEvolution()
      ]);
      
      // Handle each result safely
      if (results[0].status === 'fulfilled') {
        setOverview(results[0].value.data);
      } else {
        console.error('Failed to fetch overview:', results[0].reason);
        setOverview({ subscription_tier: 'free', symbol_dictionary: { total_symbols: 0 }, dream_climate: { dream_count: 0 }, growth_threads: { active_threads: 0 } });
      }
      
      if (results[1].status === 'fulfilled') {
        setSymbols(results[1].value.data.symbols || []);
      } else {
        console.error('Failed to fetch symbols:', results[1].reason);
        setSymbols([]);
      }
      
      if (results[2].status === 'fulfilled') {
        setClimate(results[2].value.data);
      } else {
        console.error('Failed to fetch climate:', results[2].reason);
        setClimate(null);
      }
      
      if (results[3].status === 'fulfilled') {
        setThreads(results[3].value.data.threads || []);
      } else {
        console.error('Failed to fetch threads:', results[3].reason);
        setThreads([]);
      }
      
      if (results[4].status === 'fulfilled') {
        setEvolution(results[4].value.data);
      } else {
        console.error('Failed to fetch evolution:', results[4].reason);
        setEvolution({ has_evolution: false });
      }
      
      // Check for authentication errors
      results.forEach(result => {
        if (result.status === 'rejected' && result.reason?.response?.status === 401) {
          navigate('/login');
        }
      });
    } catch (error) {
      console.error('Failed to fetch intelligence data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInsight = async (symbol) => {
    setGeneratingInsight(symbol);
    try {
      const response = await generateSymbolInsight(symbol);
      // Refresh symbols
      const symbolsRes = await getSymbolDictionary();
      setSymbols(symbolsRes.data.symbols || []);
      if (response.data.insight) {
        toast.success('Insight generated');
      } else {
        toast.info(response.data.message || 'Patterns still emerging');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate insight');
    } finally {
      setGeneratingInsight(null);
    }
  };

  const handleGenerateReflection = async () => {
    setGeneratingReflection(true);
    try {
      const response = await generateClimateReflection('month');
      setClimate(prev => ({ ...prev, reflection: response.data.reflection }));
      if (response.data.reflection) {
        toast.success('Climate reflection generated');
      } else {
        toast.info(response.data.message || 'Patterns still forming');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Failed to generate reflection');
    } finally {
      setGeneratingReflection(false);
    }
  };

  const handleDiscoverThreads = async () => {
    setDiscoveringThreads(true);
    try {
      const response = await discoverGrowthThreads();
      toast.success('Thread analysis complete');
      // Refresh threads
      const threadsRes = await getGrowthThreads();
      setThreads(threadsRes.data.threads || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Failed to discover threads');
    } finally {
      setDiscoveringThreads(false);
    }
  };

  const handleAnalyzeEvolution = async () => {
    setAnalyzingEvolution(true);
    try {
      const response = await analyzeDreamEvolution();
      if (response.data.insight) {
        setEvolution({
          has_evolution: true,
          insight: response.data.insight,
          detected_shifts: response.data.detected_shifts || [],
          earlier_period: response.data.earlier_period,
          recent_period: response.data.recent_period,
          dreams_compared: response.data.dreams_compared || 0,
          generated_at: response.data.generated_at
        });
        toast.success('Evolution insight updated');
      } else {
        toast.info(response.data.message || 'No significant evolution detected yet');
      }
    } catch (error) {
      const message = error.response?.data?.detail || error.response?.data?.message || 'Failed to analyze evolution';
      toast.error(message);
    } finally {
      setAnalyzingEvolution(false);
    }
  };

  const isDeepTier = overview?.subscription_tier === 'deep';
  const isPlusTier = overview?.subscription_tier === 'plus';
  const canAccessFull = isDeepTier || overview?.subscription_tier === 'trial';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-8" data-testid="intelligence-page">
      <TopNav />
      
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <h1 className="text-2xl font-serif font-bold">Dream Intelligence</h1>
          </div>
          <p className="text-muted-foreground">
            Patterns and insights evolving from your dream journey
          </p>
        </header>

        {/* Organic Status - Only show when there's meaningful data (symbols with 3+ occurrences) */}
        {(() => {
          const meaningfulSymbols = symbols.filter(s => s.count >= 3).length;
          const hasData = meaningfulSymbols > 0 || overview?.growth_threads?.active_threads > 0;
          
          if (!hasData) {
            return (
              <div className="glass-card p-4 mb-8 text-center">
                <p className="text-sm text-muted-foreground italic">
                  Your patterns will emerge here as you record dreams over time.
                </p>
              </div>
            );
          }
          
          return (
            <div className="glass-card p-4 mb-8">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                {meaningfulSymbols > 0 && (
                  <span className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-primary" />
                    {meaningfulSymbols} symbol{meaningfulSymbols !== 1 ? 's' : ''} surfacing
                  </span>
                )}
                {overview?.growth_threads?.active_threads > 0 && (
                  <span className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    {overview.growth_threads.active_threads} thread{overview.growth_threads.active_threads !== 1 ? 's' : ''} forming
                  </span>
                )}
              </div>
            </div>
          );
        })()}

        {/* Tab Navigation - Only show tabs with meaningful data */}
        {(() => {
          const meaningfulSymbols = symbols.filter(s => s.count >= 3).length;
          const hasClimateData = climate && climate.dream_count >= 3;
          const hasThreads = threads.length > 0;
          const hasEvolution = evolution && evolution.has_evolution;
          
          // If only symbols (or nothing), don't show tabs at all
          if (!hasClimateData && !hasThreads && !hasEvolution) {
            return null;
          }
          
          return (
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              <Button
                variant={activeTab === 'symbols' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('symbols')}
                className="rounded-full whitespace-nowrap"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Symbols
              </Button>
              {hasClimateData && (
                <Button
                  variant={activeTab === 'climate' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('climate')}
                  className="rounded-full whitespace-nowrap"
                >
                  <Cloud className="w-4 h-4 mr-2" />
                  Climate
                </Button>
              )}
              {hasThreads && (
                <Button
                  variant={activeTab === 'threads' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('threads')}
                  className="rounded-full whitespace-nowrap"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Threads
                </Button>
              )}
              {hasEvolution && (
                <Button
                  variant={activeTab === 'evolution' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('evolution')}
                  className="rounded-full whitespace-nowrap"
                >
                  <GitCompare className="w-4 h-4 mr-2" />
                  Evolution
                </Button>
              )}
            </div>
          );
        })()}

        {/* Symbol Dictionary Tab */}
        {activeTab === 'symbols' && (
          <div className="space-y-4">
            <h2 className="font-serif font-bold text-lg">Your Personal Symbols</h2>
            
            {/* Only show symbols that have appeared 3+ times (threshold for insight) */}
            {symbols.filter(s => s.count >= 3).length === 0 ? (
              <div className="glass-card p-8 text-center">
                <p className="text-sm text-muted-foreground italic">
                  Your personal symbols will emerge as patterns repeat.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {symbols.filter(s => s.count >= 3).map((sym) => (
                  <div key={sym.symbol} className="glass-card p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-lg capitalize">{sym.symbol[0]}</span>
                        </div>
                        <div>
                          <h3 className="font-serif font-bold capitalize">{sym.symbol}</h3>
                          <p className="text-xs text-muted-foreground">
                            {sym.count} occurrence{sym.count !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      {canAccessFull && sym.count >= 3 && !sym.personal_insight && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleGenerateInsight(sym.symbol)}
                          disabled={generatingInsight === sym.symbol}
                          className="text-primary"
                        >
                          {generatingInsight === sym.symbol ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                    
                    {sym.personal_insight ? (
                      <p className="text-sm text-muted-foreground italic leading-relaxed">
                        &ldquo;{sym.personal_insight}&rdquo;
                      </p>
                    ) : canAccessFull ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleGenerateInsight(sym.symbol)}
                        disabled={generatingInsight === sym.symbol}
                        className="mt-2 text-primary p-0 h-auto"
                      >
                        {generatingInsight === sym.symbol ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                            Reflecting...
                          </>
                        ) : (
                          <>
                            <Eye className="w-3 h-3 mr-2" />
                            Reflect on this symbol
                          </>
                        )}
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2 mt-2">
                        <Lock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Insights with ONIRVA Deep</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Dream Climate Tab - Only shown when threshold met (3+ dreams) */}
        {activeTab === 'climate' && climate && climate.dream_count >= 3 && (
          <div className="space-y-4">
            <h2 className="font-serif font-bold text-lg">Dream Climate</h2>

            {/* Climate Overview Card */}
            <div className="glass-card p-6">
              {climate.reflection ? (
                <p className="text-muted-foreground italic leading-relaxed mb-4">
                  &ldquo;{climate.reflection}&rdquo;
                </p>
              ) : canAccessFull ? (
                <Button
                  variant="ghost"
                  onClick={handleGenerateReflection}
                  disabled={generatingReflection}
                  className="text-primary p-0 h-auto mb-4"
                >
                  {generatingReflection ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Reflecting...
                    </>
                  ) : (
                    'Generate reflection'
                  )}
                </Button>
              ) : null}

              {/* Mood Distribution */}
              {Object.keys(climate.mood_distribution || {}).length > 0 && (
                <div className="mb-4">
                  <h4 className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Emotional Tones</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(climate.mood_distribution).map(([mood, count]) => (
                      <span key={mood} className="px-3 py-1 rounded-full bg-muted text-xs capitalize">
                        {mood}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Symbols */}
              {climate.top_symbols?.length > 0 && (
                <div>
                  <h4 className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Recurring Symbols</h4>
                  <div className="flex flex-wrap gap-2">
                    {climate.top_symbols.map(({ symbol }) => (
                      <span key={symbol} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs capitalize">
                        {symbol}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Essences */}
            {climate.essences?.length > 0 && canAccessFull && (
              <div className="glass-card p-4">
                <h4 className="text-xs text-muted-foreground mb-3 uppercase tracking-wider">Dream Essences</h4>
                <div className="space-y-2">
                  {climate.essences.map((essence, idx) => (
                    <p key={idx} className="text-sm text-muted-foreground italic border-l-2 border-primary/30 pl-3">
                      {essence}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Growth Threads Tab - Only shown when threads exist */}
        {activeTab === 'threads' && threads.length > 0 && (
          <div className="space-y-4">
            <h2 className="font-serif font-bold text-lg">Growth Threads</h2>

            <div className="space-y-3">
              {threads.map((thread, idx) => (
                <div key={idx} className="glass-card p-4">
                  {thread.observation && (
                    <p className="text-muted-foreground italic leading-relaxed">
                      &ldquo;{thread.observation}&rdquo;
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dream Evolution Tab - Only shown when evolution insight exists */}
        {activeTab === 'evolution' && evolution && evolution.has_evolution && (
          <div className="space-y-4">
            <h2 className="font-serif font-bold text-lg">Dream Evolution</h2>
            <p className="text-sm text-muted-foreground mb-4">
              How your inner landscape has shifted over time
            </p>

            {/* Main Evolution Insight */}
            <div className="glass-card p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <GitCompare className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-serif font-medium text-sm text-muted-foreground mb-1">
                    A shift observed
                  </h3>
                </div>
              </div>
              
              <p className="text-muted-foreground italic leading-relaxed text-base">
                &ldquo;{evolution.insight}&rdquo;
              </p>

              {/* Period Context */}
              {(evolution.earlier_period || evolution.recent_period) && (
                <div className="mt-6 pt-4 border-t border-border/50">
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                    {evolution.earlier_period && (
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-muted-foreground/30"></span>
                        <span>Earlier: {evolution.earlier_period}</span>
                      </div>
                    )}
                    {evolution.recent_period && (
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-primary/50"></span>
                        <span>Recent: {evolution.recent_period}</span>
                      </div>
                    )}
                    {evolution.dreams_compared > 0 && (
                      <div className="flex items-center gap-2">
                        <span>{evolution.dreams_compared} dreams compared</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Detected Shifts */}
              {evolution.detected_shifts && evolution.detected_shifts.length > 0 && (
                <div className="mt-6 pt-4 border-t border-border/50">
                  <h4 className="text-xs text-muted-foreground mb-3 uppercase tracking-wider">Areas of Change</h4>
                  <div className="flex flex-wrap gap-2">
                    {evolution.detected_shifts.map((shift, idx) => (
                      <span key={idx} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs capitalize">
                        {shift}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Re-analyze Option for Deep Tier */}
            {canAccessFull && (
              <div className="text-center mt-6">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAnalyzeEvolution}
                  disabled={analyzingEvolution}
                  className="text-muted-foreground hover:text-primary"
                >
                  {analyzingEvolution ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Observing changes...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Re-analyze evolution
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Available once per month
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
